"use client";

import * as React from "react";
import {
  Grid,
  Button,
  Box,
  Typography,
  FormControl,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../authentication/AuthContext";
import config from "../../config";

const SignInForm = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // prevent page reload
    setLoading(true);
    setError("");

    // Get email & password values
    const email = e.target.email.value;
    const password = e.target.password.value;

    try {
      // Make API call to login endpoint
      const response = await fetch(`${config.API_BASE_URL}/client/auth/login`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          email_id: email,
          password: password,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // ✅ Store the actual API key from backend response
        const apiKey = data.data.api_key;

        // UNIFIED AUTH: Use the real JWT from backend
        // This is now signed by the server key, not a client mock
        const jwtToken = data.data.access_token;

        if (!jwtToken) {
          throw new Error("No access token received from server");
        }

        // ✅ Store user data in config including access token
        storeUserData(data.data);

        // Log in via AuthContext
        await login(jwtToken, data.data);

        // Navigation is handled by login function
      } else {
        setError(data.message || "Login failed");
      }
    } catch (err) {
      setError("Network error. Please try again.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };



  // Store user data for use across the application
  const storeUserData = (userData) => {
    // Store in localStorage for persistence
    localStorage.setItem("userData", JSON.stringify(userData));

    // ✅ Update config with the actual user data including api_key
    config.setUserData(userData);

    console.log("User data stored:", {
      apiKey: userData.api_key,
      clientId: userData.client_id,
      clientUserId: userData.client_user_id
    });
  };

  return (
    <>
      <div className="fp-wrapper">
        <Box
          className="auth-main-wrapper sign-in-area"
          sx={{
            py: { xs: "60px", md: "80px", lg: "100px", xl: "135px" },
            background: "linear-gradient(135deg, #E0ECFF 0%, #F3E8FF 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Box
            sx={{
              maxWidth: { sm: "500px", md: "1255px" },
              mx: "auto !important",
              px: "12px",
              width: "100%",
            }}
          >
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              columnSpacing={{ xs: 1, sm: 2, md: 4, lg: 3 }}
            >
              <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 5 }}>
                <Box
                  className="form-content"
                  sx={{
                    paddingLeft: { xs: "0", lg: "10px" },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Box
                    className="logo"
                    sx={{
                      mb: "23px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src="/images/Bud-e-logo-removebg.png"
                      alt="logo"
                      width={112}
                      height={28}
                    />
                  </Box>

                  <Box
                    className="title"
                    sx={{
                      mb: "23px",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="h1"
                      className="text-black"
                      sx={{
                        fontSize: { xs: "22px", sm: "25px", lg: "28px" },
                        mb: "7px",
                        fontWeight: "600",
                      }}
                    >
                      Good to see you again!
                    </Typography>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
                    <Box mb="15px">
                      <FormControl fullWidth>
                        <Typography
                          component="label"
                          sx={{
                            fontWeight: "500",
                            fontSize: "14px",
                            mb: "10px",
                            display: "block",
                            textAlign: "left",
                          }}
                          className="text-black"
                        >
                          Email Address
                        </Typography>

                        <TextField
                          label="example&#64;email.com"
                          variant="filled"
                          id="email"
                          name="email"
                          type="email"
                          required
                          sx={{
                            "& .MuiInputBase-root": {
                              border: "1px solid #D5D9E2",
                              backgroundColor: "#fff",
                              borderRadius: "7px",
                            },
                            "& .MuiInputBase-root::before": {
                              border: "none",
                            },
                            "& .MuiInputBase-root:hover::before": {
                              border: "none",
                            },
                            "& .MuiInputBase-root:hover:hover:not(.Mui-disabled, .Mui-error)::before":
                            {
                              border: "none",
                            },
                          }}
                        />
                      </FormControl>
                    </Box>

                    <Box mb="15px">
                      <FormControl fullWidth>
                        <Typography
                          component="label"
                          sx={{
                            fontWeight: "500",
                            fontSize: "14px",
                            mb: "10px",
                            display: "block",
                            textAlign: "left",
                          }}
                          className="text-black"
                        >
                          Password
                        </Typography>

                        <TextField
                          label="Type Password"
                          variant="filled"
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                >
                                  <i className="material-symbols-outlined">
                                    {showPassword ? "visibility" : "visibility_off"}
                                  </i>
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiInputBase-root": {
                              border: "1px solid #D5D9E2",
                              backgroundColor: "#fff",
                              borderRadius: "7px",
                            },
                            "& .MuiInputBase-root::before": {
                              border: "none",
                            },
                            "& .MuiInputBase-root:hover::before": {
                              border: "none",
                            },
                            "& .MuiInputBase-root:hover:hover:not(.Mui-disabled, .Mui-error)::before":
                            {
                              border: "none",
                            },
                          }}
                        />
                      </FormControl>
                    </Box>

                    <Box mb="20px" sx={{ textAlign: "left" }}>
                      <Link
                        to="/authentication/forgot-password/"
                        className="text-primary"
                        style={{
                          fontWeight: "500",
                        }}
                      >
                        Forgot Password?
                      </Link>
                    </Box>

                    <Box mb="20px">
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                          textTransform: "capitalize",
                          borderRadius: "6px",
                          fontWeight: "500",
                          fontSize: { xs: "13px", sm: "16px" },
                          padding: { xs: "10px 20px", sm: "10px 24px" },
                          color: "#fff !important",
                          boxShadow: "none",
                          width: "100%",
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          <>
                            <i className="material-symbols-outlined mr-5">login</i>
                            Sign In
                          </>
                        )}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </div>
    </>
  );
};

export default SignInForm;